import spartacusOnboardingAPI from '@api/jooicez-backend/spartacus/onboarding';
import Iconify from '@components/iconify';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import icons from '@utils/icons';
import { useState } from 'react';
import { useApp } from 'src/contexts/ApplicationContext';
import { useResponsive } from 'src/hooks/use-responsive';

import PreferenceCardGrid from '../components/PreferenceCardGrid';

import { StepProps } from './steps';

const COMPANY_SIZE_CONTENT = 'company_size';

const OPTIONS = [
  { key: 'just_me', label: 'Just me', icon: icons.commons.user },
  { key: '1_3', label: '1–3 team members', icon: icons.commons.users },
  { key: '3_5', label: '3–5 team members', icon: icons.commons.usersThree },
  { key: '5_more', label: 'More than 5 members', icon: icons.commons.usersFour },
] as const;

export default function TeamSizeStep({ onNext }: StepProps) {
  const { organizationId } = useApp();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useResponsive('down', 'sm');

  const toggleOption = (key: string) => {
    setSelectedOptions((prev) => (prev.includes(key) ? [] : [key]));
  };

  const handleContinue = async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      await Promise.all(
        selectedOptions.map((key) => {
          const option = OPTIONS.find((o) => o.key === key);
          if (!option) return Promise.resolve();
          return spartacusOnboardingAPI.createSpecialization(organizationId, {
            key: option.key,
            content: COMPANY_SIZE_CONTENT,
          });
        }),
      );
      onNext();
    } catch (error) {
      console.error('Error saving team size:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        pt: { xs: 4, sm: 0 },
        gap: 4,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
        <Typography variant={isMobile ? 'h2' : 'h1'} color="text.primary">
          <Box component="span" sx={{ color: 'secondary.darker' }}>
            What is the size
          </Box>{' '}
          of your MedSpa team?
        </Typography>
      </Box>

      <PreferenceCardGrid options={OPTIONS} selectedKeys={selectedOptions} onToggle={toggleOption} singleRow />

      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={selectedOptions.length === 0 || isLoading}
        onClick={handleContinue}
        endIcon={!isLoading ? <Iconify icon={icons.arrows.right} /> : undefined}
        sx={{ minWidth: { xs: '100%', sm: 200 } }}
      >
        {isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} color="inherit" />
            <span>Saving...</span>
          </Stack>
        ) : (
          'Continue'
        )}
      </Button>
    </Box>
  );
}
